// src/components/ReminderList.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchData, updateData } from "../services/api";
import { chuNgayAmDayDu } from "../utils/amlich";
import { DangTai } from "../vo";

// ============================================================
// 🔔 NHẮC NHỞ HÔM NAY — đọc từ HÀNG ĐỢI `nhac_nho` (Đợt N4, 14/09/2026)
// ------------------------------------------------------------
// Máy chủ trả về mọi lời nhắc ĐANG HIỆU LỰC của người đang xem, mỗi dòng có
// `loai_nguon`: 'event' | 'task' | 'chi_tieu'. Khung này chỉ vẽ HAI loại:
//
//   💰 chi_tieu — "Ba báo cả nhà xem chi tiêu tháng 9". Bấm vào là mở trang
//                 Báo chi tiêu; trang đó tự báo "đã xem" nên dòng tự hết.
//   📅 event    — kèm "còn N ngày" / "HÔM NAY". Qua ngày sự kiện là máy chủ
//                 tự bỏ, không cần ai bấm gì.
//
// ⚠️ VIỆC NHÀ ('task') CỐ Ý KHÔNG VẼ Ở ĐÂY. Anh chốt 14/09 (câu 3): việc đến
// ngày nhắc hiện ở khối "👋 Người nhà đang nhờ bạn", gắn 🔔 — khối đó vốn đã
// liệt kê mọi việc chưa làm, vẽ thêm ở đây là một việc hiện hai lần trên
// cùng một màn hình. Xem ViecNhoBan.js.
//
// ⚠️ NÚT 🔕 TẮT NHẮC chỉ có ở SỰ KIỆN, và tắt cho CẢ NHÀ (anh chọn giữ như
// cũ, câu 6). Việc nhà không tắt được — muốn hết nhắc thì làm xong.
//
// "Tắt nhắc" ≠ "Đã làm xong" (chân dung 5.7). Nút này chỉ tắt chuông.
// ============================================================

const veNgay = (chuoi) => {
  // "2026-09-30" -> "30/9". Tách chuỗi, KHÔNG đưa qua new Date() — bẫy múi giờ.
  if (!chuoi) return "";
  const [, thang, ngay] = chuoi.split("-").map(Number);
  return `${ngay}/${thang}`;
};

const chuConLai = (n) => {
  if (n === 0) return "HÔM NAY";
  if (n === 1) return "ngày mai";
  return `còn ${n} ngày`;
};

function ReminderList() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dangTat, setDangTat] = useState(null); // id lời nhắc đang xử lý
  const [loi, setLoi] = useState("");

  useEffect(() => {
    fetchData("reminders")
      .then((data) =>
        setReminders(
          (Array.isArray(data) ? data : []).filter((r) => r.loai_nguon !== "task"),
        ),
      )
      .catch((error) => console.error("Lỗi tải nhắc nhở:", error))
      .finally(() => setLoading(false));
  }, []);

  const tatLoiNhac = async (item) => {
    setDangTat(item.id);
    try {
      await updateData("nhac-nho", `${item.id}/tat`, {});
      // Bỏ khỏi danh sách ngay tại chỗ, không phải tải lại cả trang
      setReminders((cu) => cu.filter((r) => r.id !== item.id));
    } catch (err) {
      setLoi("Không tắt được lời nhắc này. Bạn thử lại giúp.");
      setTimeout(() => setLoi(""), 4000);
    } finally {
      setDangTat(null);
    }
  };

  if (loading) {
    return <DangTai gi="nhắc nhở" />;
  }

  if (reminders.length === 0) {
    return (
      <div className="reminder-empty">✅ Không có nhắc nhở nào hôm nay.</div>
    );
  }

  const coSuKien = reminders.some((r) => r.loai_nguon === "event");

  return (
    <div className="reminder-card">
      <h3 className="reminder-title">🔔 Nhắc nhở hôm nay</h3>
      {coSuKien && (
        <p className="reminder-ghi-chu">
          Nút <strong>Tắt nhắc</strong> ẩn lời nhắc với <strong>cả nhà</strong>,
          không phải báo đã làm xong.
        </p>
      )}

      {loi && <div className="reminder-loi">{loi}</div>}

      <ul className="reminder-list">
        {reminders.map((item) =>
          item.loai_nguon === "chi_tieu" ? (
            <li key={item.id} className="reminder-item">
              <Link to="/chi-tieu" className="reminder-chi-tieu">
                💰 {item.noi_dung}
                <span className="reminder-chi-tieu-mo">Vào xem chi tiêu tháng này →</span>
              </Link>
            </li>
          ) : (
            <li key={item.id} className="reminder-item">
              <span className="reminder-name">🔹 {item.noi_dung}</span>
              <span className="reminder-type">Sự kiện</span>
              <span className="reminder-date">
                📅 {veNgay(item.het_han)}
                {/* Thêm "T00:00:00" để trình duyệt đọc theo giờ ĐỊA PHƯƠNG.
                    Chuỗi trơn "2026-09-30" bị đọc là nửa đêm giờ UTC. */}
                <span className="ngay-am">
                  {item.het_han ? chuNgayAmDayDu(`${item.het_han}T00:00:00`) : ""}
                </span>
                {" · "}
                <span className="reminder-con-lai">{chuConLai(item.con_ngay)}</span>
              </span>

              <button
                onClick={() => tatLoiNhac(item)}
                disabled={dangTat === item.id}
                title="Tắt lời nhắc này cho cả nhà (KHÔNG phải báo đã làm xong việc)"
                className="reminder-nut-tat"
              >
                {dangTat === item.id ? "..." : "🔕 Tắt nhắc"}
              </button>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}

export default ReminderList;
